import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { PageSubTitle } from 'Components/Titles';
import { PromoCodeForm } from 'Forms/PromoCodeForm/PromoCodeForm';
import {
  MILITARYGIFT_COUPON,
  MILITARYDEAL_COUPON,
  RETENTION_YEAR_PROMO,
  RETENTION_3_MONTHS_PROMO,
  SPRING_25_OFF_PROMO,
} from 'Utils/Constants';
import { scrollToElementWithDelay } from 'Utils/Utils';
import { getDefaultConfig } from 'Utils/Store';
import { SmartLink } from 'Components/SmartLink';

export const ManagePromotion = forwardRef(({ onSubmitPromoCode, validPromoMsg }, ref) => {
  const promoWrapper = useRef(null);
  const [isScrolled, setScrolled] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const promoCodeForm = useRef(null);

  const { termsOfUsePage } = getDefaultConfig();

  const handleInitialize = (initialValue, forceScroll = false, delay = 750) => {
    if ((!isScrolled || forceScroll) && promoWrapper && promoWrapper.current) {
      setCouponCode(initialValue);
      setScrolled(true);

      // targets redfast banner above ManagePromotion component.
      scrollToElementWithDelay(promoWrapper.current.previousElementSibling, delay);
    }
  };

  useImperativeHandle(ref, () => ({
    setTmtBannerCode: (code) => {
      promoCodeForm?.current?.setFormFieldValue(code);
      handleInitialize(code, true, 300);
    },
  }));

  const subTitle = (
    <>
      Promo<span className="mobileOnly">tion</span> Code
    </>
  );

  return (
    <div className="sectionFlex promotionSection" ref={promoWrapper}>
      <div className="flexGrowOne settingsSectionLeft">
        <PageSubTitle text={subTitle} />
      </div>
      <div className="sectionFlex flexDirCol flexGrowTwo settingsSectionRight">
        <div className="sectionFlex promoCodeSection">
          <PromoCodeForm
            ref={promoCodeForm}
            submitButtonText="Apply"
            onFormSubmit={onSubmitPromoCode}
            validPromoMsg={validPromoMsg}
            onChange={setCouponCode}
            onInitialize={handleInitialize}
            canAutoApplyCoupon={false}
            displaySuccessPopup={true}
          />
        </div>
        {(couponCode.toUpperCase() === MILITARYGIFT_COUPON || couponCode.toUpperCase() === 'DOWNLOAD') && (
          <div className="sectionFlex promoCodeLegalText">
            *By clicking &quot;Apply&quot;, you will immediately be billed $32.49, and your two-year renewal offer
            period will begin. Any remaining months from your first free year will be applied at the end of this
            two-year renewal offer period. After the two-year renewal period and the remaining months from your first
            free year expires, your subscription will automatically renew at the then-current rates for Military &amp;
            Vet subscription plans for one year, unless you cancel 24 hours before renewal date. The renewal charges
            will be applied to the payment method associated with your account, and you hereby authorize Fox News
            Network, LLC or its designee to make such charge. We will send a confirmation to the email address
            associated with your account describing the plan change and how to cancel before the next renewal.
          </div>
        )}
        {couponCode.toUpperCase() === MILITARYDEAL_COUPON && (
          <div className="sectionFlex promoCodeLegalText">
            *By clicking &quot;Apply&quot;, you will immediately be billed $19.50, and your one-year renewal offer
            period will begin. Any remaining months from your first free year will be applied at the end of this
            one-year renewal offer period. After the one-year renewal period and the remaining months from your first
            free year expires, your subscription will automatically renew at the then-current rates for Military &amp;
            Veterans subscription plans for one year, unless you cancel 24 hours before renewal date. The renewal
            charges will be applied to the payment method associated with your account, and you hereby authorize Fox
            News Network, LLC or its designee to make such charge. We will send a confirmation to the email address
            associated with your account describing the plan change and how to cancel before the next renewal.
          </div>
        )}
        {couponCode.toUpperCase() === RETENTION_YEAR_PROMO && (
          <div className="sectionFlex promoCodeLegalText">
            *By clicking &quot;Apply&quot;, you will immediately be billed $19.99 plus tax, and your one-year renewal
            period will begin. Any months remaining on your current subscription will be added at the end of your
            one-year renewal period. After the one-year renewal period expires, your subscription will automatically
            renew at the then-current rates for Fox Nation Yearly plan, unless you cancel 24 hours before renewal date.
            The renewal charges will be applied to the payment method associated with your account, and you hereby
            authorize Fox News Network, LLC or its designee to make such charge. We will send a confirmation to the
            email address associated with your account describing the plan change and how to cancel before the next
            renewal.
          </div>
        )}
        {couponCode.toUpperCase() === RETENTION_3_MONTHS_PROMO && (
          <div className="sectionFlex promoCodeLegalText">
            *By clicking &quot;Apply&quot;, you will immediately be billed $2.97 plus tax, and your 3-month discounted
            period will begin immediately. Any time remaining on your current subscription will be added at the end of
            your 3-month discounted period. After the 3-month discounted period expires, your subscription will
            automatically renew at the then-current rates for the Fox Nation Monthly plan, unless you cancel 24 hours
            before the renewal date. The renewal charges will be applied to the payment method associated with your
            account, and you hereby authorize Fox News Network, LLC or its designee to make such charge. We will send a
            confirmation to the email address associated with your account describing the plan change and how to cancel
            before the next renewal.
          </div>
        )}
        {couponCode.toUpperCase() === SPRING_25_OFF_PROMO && (
          <div className="sectionFlex promoCodeLegalText">
            <span>
              *By clicking on &quot;Apply,&quot; you will immediately be billed $48.74 and your one-year renewal period
              will begin. Any remaining months from your first year will be applied at the end of this renewal offer
              period. After your renewal period and the remaining months from your first year expires, your subscription
              will automatically renew at the then-current rates for yearly subscription plans, unless you cancel 24
              hours before your renewal date. The renewal charges will be applied to the payment method associated with
              your account, and you hereby authorize Fox News Network, LLC or its designee to make such charge. We will
              send a confirmation to the email address associated with your account describing the plan change and how
              to cancel before the next renewal. See our{' '}
              <SmartLink linkUrl={termsOfUsePage} target="noopener noreferrer" reload text="Terms of Use" /> for
              complete and current information.
            </span>
          </div>
        )}
      </div>
    </div>
  );
});
